import { Suspense, lazy, useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { apiFetch, setExpectedTenantId, setTenantChangedHandler } from "../lib/api.js";
import { subscribeTenantSelected } from "../lib/tenantSession.js";
import { AppShell } from "../components/AppShell.js";
import { TenantOverview } from "./TenantOverview.js";
import { Explore } from "./Explore.js";
import { Account } from "./Account.js";
import { HelpDocs } from "./HelpDocs.js";
import { TenantWhatsNew } from "./TenantWhatsNew.js";
import { ReportEditor } from "./ReportEditor.js";
import { DataProviders } from "./DataProviders.js";
import { DataProviderEditor } from "./DataProviderEditor.js";
import { Visualizations } from "./Visualizations.js";
import { TenantAdminPeople } from "./TenantAdminPeople.js";
import { Notifications } from "./Notifications.js";
import { NotificationDetail } from "./NotificationDetail.js";
import { SettingsNotifications } from "./SettingsNotifications.js";
import { ActionItemDetail } from "./ActionItemDetail.js";
import { ActionItemsList, RASCI_ROLES } from "./ActionItemsList.js";
import { ActionItemCreate } from "./ActionItemCreate.js";
import { AdminActionItemCodes } from "./AdminActionItemCodes.js";
import { Customers } from "./Customers.js";
import { CustomerNew } from "./CustomerNew.js";
import { CustomerDetail } from "./CustomerDetail.js";
import { Integrations } from "./Integrations.js";
import { IntegrationNew } from "./IntegrationNew.js";
import { IntegrationDetail } from "./IntegrationDetail.js";
import { AdminEscalationPolicies } from "./AdminEscalationPolicies.js";
import { AdminEscalationPolicyEditor } from "./AdminEscalationPolicyEditor.js";
import { AdminNotificationRouting } from "./AdminNotificationRouting.js";
import { TenantAdminConnectors } from "./TenantAdminConnectors.js";
import { TenantAdminAiUsage } from "./TenantAdminAiUsage.js";
import { TenantAdminLearning } from "./TenantAdminLearning.js";
import { TenantAdminTenantKnowledge, TenantKnowledgeEntryEditor } from "./TenantAdminTenantKnowledge.js";
import { TenantGuidanceEntryEditor } from "./TenantGuidanceEntryEditor.js";
import { TenantKnowledgeProposalDetail } from "./TenantKnowledgeProposedTab.js";
import { TenantAdminSettings } from "./TenantAdminSettings.js";
import { TenantActivity } from "./TenantActivity.js";
import { TenantActivityCard } from "./TenantActivityCard.js";
import { TenantSubjectDetail } from "./TenantSubjectDetail.js";
import { TenantSubjectPerson } from "./TenantSubjectPerson.js";
import { BuildPages } from "./BuildPages.js";
import { BuildPageEditor } from "./BuildPageEditor.js";
import { BuildMenus } from "./BuildMenus.js";
import { BuildMenuEditor } from "./BuildMenuEditor.js";
import { WorkspacePages } from "./WorkspacePages.js";
import { WorkspaceMenus } from "./WorkspaceMenus.js";
import { TenantAtlasReports } from "./TenantAtlasReports.js";
import { TenantAtlasReportDetail } from "./TenantAtlasReportDetail.js";
import { WorkspacePDOs } from "./WorkspacePDOs.js";
import { NewUploadPdo } from "./NewUploadPdo.js";
import { WorkspaceSchedules } from "./WorkspaceSchedules.js";
// Saved Queries UI lives inside Atlas Query per ADR-0068 Phase 7. No standalone route.
import { OrphanedContent } from "./OrphanedContent.js";
import { TenantContentTransfer } from "./TenantContentTransfer.js";
import { OperationsList } from "./OperationsList.js";
import { OperationEditor } from "./OperationEditor.js";
import { SemanticModels } from "./SemanticModels.js";
import { SemanticDesigner } from "./SemanticDesigner.js";
import { PersistentDataObjects } from "./PersistentDataObjects.js";
import { PdoDetail } from "./PdoDetail.js";
import { WorkspaceModels } from "./WorkspaceModels.js";
import { ModelDetail } from "./ModelDetail.js";
import { TenantPage } from "./TenantPage.js";
import { TenantDefaultPage } from "./TenantDefaultPage.js";
import { ScheduleWorkflows } from "./ScheduleWorkflows.js";
import { TenantSubscriptions } from "./TenantSubscriptions.js";
import { TenantStudies } from "./TenantStudies.js";
import { TenantInitiatives } from "./TenantInitiatives.js";
import { TenantInitiativeCreate } from "./TenantInitiativeCreate.js";
import { TenantInitiativeDetail } from "./TenantInitiativeDetail.js";
import { TenantIssues } from "./TenantIssues.js";
import { TenantIssueCreate } from "./TenantIssueCreate.js";
import { TenantIssueDetail } from "./TenantIssueDetail.js";
import { CollabWeek } from "./CollabWeek.js";
import { CollabIdeas } from "./CollabIdeas.js";
import { CollabIdeaCreate } from "./CollabIdeaCreate.js";
import { CollabIdeaDetail } from "./CollabIdeaDetail.js";
import { CollabMeetings } from "./CollabMeetings.js";
import { CollabSession } from "./CollabSession.js";
import { CollabSeriesEdit } from "./CollabSeriesEdit.js";
import { CollabSeries } from "./CollabSeries.js";
import { TenantStudyDetail } from "./TenantStudyDetail.js";
import { legacyBoardRedirect, modelHomePath, type OptimizeModelRef } from "../lib/optimizeNav.js";
import { OptimizeHome } from "./OptimizeHome.js";
import { OptimizeModelHome } from "./OptimizeModelHome.js";
import { OptimizeProposal } from "./OptimizeProposal.js";
import { TenantSubscriptionNew } from "./TenantSubscriptionNew.js";
import { TenantSubscriptionDetail } from "./TenantSubscriptionDetail.js";
import { ScheduleWorkflowCanvas } from "./ScheduleWorkflowCanvas.js";
import { ScheduleBlackouts } from "./ScheduleBlackouts.js";
import { ScheduleActivity } from "./ScheduleActivity.js";
import { MonitorWatchOperations } from "./MonitorWatchOperations.js";
import { atlasQueryOpen } from "../lib/atlasQueryAccess.js";
const AtlasQuery = lazy(async () => {
  const mod = await import("./AtlasQuery.js");
  return { default: mod.AtlasQuery };
});

type TenantMe = {
  user: {
    id: string;
    email: string;
    role: string;
    entitlement?: string;
    status: string;
    /** ADR-0213 ruling 84 — Atlas Query switched on for them. */
    atlas_query?: boolean;
  };
  /** The landing zone's bounds; `lib/pdoUploadApi.ts` `getUploadLimits` reads them. */
  upload_limits?: { tenant_budget_bytes: number; inline_read_max_bytes?: number };
  // The tenant this session is actually on. memberships is ALL of the user's
  // tenants (ordered by tenant creation date) — never use memberships[0] to
  // identify the active tenant.
  active_tenant?: {
    tenant_id: string;
    tenant_name: string;
    tenant_slug: string;
  } | null;
  memberships: Array<{
    tenant_id: string;
    tenant_name: string;
    tenant_slug: string;
    auth_mode: string;
  }>;
  // ADR-0185 — content-driven nav probe: active optimization models the caller
  // can read. Rides this bootstrap fetch; the Optimize entry renders for every
  // role when non-empty, and Addendum 2 routes off the same list. Addendum 5
  // renamed the wire key (the hub dual-serves the old `schedule_board_models`
  // for one deploy cycle; the console reads only the new key).
  optimize_board_models?: OptimizeModelRef[];
};

type HomeTarget =
  | { kind: "page"; route_key: string }
  | { kind: "default_page" };

function ReportsDetailRedirect() {
  const params = useParams<{ reportId?: string }>();
  return <Navigate to={`/app/build/visualizations/${params.reportId ?? ""}`} replace />;
}

function RedirectTenantAdminGroups() {
  return <Navigate to="/app/admin/users?tab=groups" replace />;
}

function RedirectTenantAdminGroupDetail() {
  const { groupId = "" } = useParams<{ groupId: string }>();
  return <Navigate to={`/app/admin/users?tab=groups&group=${encodeURIComponent(groupId)}`} replace />;
}

/* ADR-0185 Addendum 4 — the board is the model home's default tab; the old
   board address redirects into it. */
function LegacyModelBoardRedirect() {
  const { modelId = "" } = useParams<{ modelId: string }>();
  return <Navigate to={modelHomePath(modelId)} replace />;
}


export function TenantApp() {
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined);
  const [tenantName, setTenantName] = useState("Tenant");
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [tenantId, setTenantId] = useState<string | undefined>(undefined);
  const [entitlement, setEntitlement] = useState<string | undefined>(undefined);
  // ADR-0213 ruling 84 — Atlas Query is a switch on the person; closed unless /tenant/me says it is on.
  const [atlasQuery, setAtlasQuery] = useState(false);
  // ADR-0185 Addendum 2 — the active models this caller can read, from the
  // bootstrap fetch. Drives the content-driven Optimize nav entry, the
  // `/app/schedule-board` redirect, and the viewer's one-click fast path.
  const [optimizeModels, setOptimizeModels] = useState<OptimizeModelRef[]>([]);
  // Set when another tab has switched this browser's session to a different
  // tenant (detected via BroadcastChannel or a rejected request). Replaces the
  // whole app with a hand-off screen — continuing to render here would show
  // one tenant's UI over another tenant's session.
  const [takenOverBy, setTakenOverBy] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const me = await apiFetch<TenantMe>("/api/tenant/me");
        setUserEmail(me.user.email);
        setUserId(me.user.id);
        // active_tenant is authoritative; memberships[0] is only a fallback
        // for a hub that predates it.
        const active = me.active_tenant ?? me.memberships[0] ?? null;
        setTenantName(active?.tenant_name ?? "Tenant");
        setTenantId(active?.tenant_id);
        setExpectedTenantId(active?.tenant_id ?? null);
        // Prefer entitlement, fallback to role for backward compatibility
        // Default to "viewer" if missing (should not happen after backend work)
        const rawEntitlement = me.user.entitlement ?? me.user.role ?? "viewer";
        setEntitlement(rawEntitlement);
        setAtlasQuery(me.user.atlas_query === true);
        setOptimizeModels(me.optimize_board_models ?? []);
        setReady(true);
      } catch {
        nav("/login");
      }
    })();
  }, [nav]);

  useEffect(() => {
    setTenantChangedHandler((activeTenantName) => setTakenOverBy(activeTenantName ?? ""));
    return () => {
      setTenantChangedHandler(null);
      setExpectedTenantId(null);
    };
  }, []);

  useEffect(() => {
    if (!tenantId) return;
    return subscribeTenantSelected((msg) => {
      if (msg.tenant_id !== tenantId) setTakenOverBy(msg.tenant_name);
    });
  }, [tenantId]);

  if (takenOverBy !== null) {
    const label = takenOverBy.trim();
    return (
      <div className="centerPage">
        <div className="card pageNarrow wFull">
          <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>
            Signed in elsewhere
          </div>
          <div className="help" style={{ marginBottom: 12 }}>
            {label
              ? `This browser is now signed in to ${label}. This tab was showing ${tenantName} and has stopped to avoid mixing the two.`
              : `This browser is now signed in to a different workspace. This tab was showing ${tenantName} and has stopped to avoid mixing the two.`}
          </div>
          <div className="actions">
            <button className="btn btnPrimary" type="button" onClick={() => location.assign("/app")}>
              {label ? `Continue in ${label}` : "Continue"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="card">
        <div className="muted">Loading…</div>
      </div>
    );
  }

  return (
    <AppShell
      contextLabel={tenantName}
      userEmail={userEmail}
      accountPath={null}
      navVariant="tenant"
      navItems={[]}
      tenantId={tenantId}
      userId={userId}
      tenantEntitlement={entitlement}
      optimizeAvailable={optimizeModels.length > 0}
      atlasQueryAvailable={atlasQueryOpen(entitlement, atlasQuery)}
    >
      <Routes>
        <Route path="/" element={<TenantHome entitlement={entitlement} />} />
        <Route path="/home" element={<TenantHome entitlement={entitlement} />} />
        <Route path="/overview" element={<TenantOverview />} />
        <Route path="/ask" element={<Navigate to="/app/home" replace />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/pages/default" element={<TenantDefaultPage entitlement={entitlement} />} />
        <Route path="/pages/:routeKey" element={<TenantPage entitlement={(entitlement as "viewer" | "builder" | "admin") ?? "viewer"} />} />
        <Route path="/account" element={<Account />} />
        <Route path="/help" element={<HelpDocs />} />
        {/* ADR-0224 §3.6 — What's new: the platform's shipped changes, read-only, every member. */}
        <Route path="/whats-new" element={<TenantWhatsNew />} />
        <Route path="/admin/users" element={entitlement === "admin" ? <TenantAdminPeople /> : <Navigate to="/app/overview" replace />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/notifications/:id" element={<NotificationDetail />} />
        <Route path="/settings/notifications" element={<SettingsNotifications />} />
        <Route
          path="/action-items"
          element={userId ? <ActionItemsList entitlement={entitlement} callerAccountId={userId} /> : null}
        />
        <Route
          path="/action-items/new"
          element={
            entitlement === "builder" || entitlement === "admin" ? (
              <ActionItemCreate />
            ) : (
              <Navigate to="/app/action-items" replace />
            )
          }
        />
        <Route path="/action-items/:id" element={<ActionItemDetail />} />
        {/* ADR-0185 Addendum 2 — the board is addressed by its model now; this
            URL no longer names anything on its own. */}
        <Route path="/schedule-board" element={<Navigate to={legacyBoardRedirect(optimizeModels)} replace />} />
        {/* ADR-0143 — Studies: every membership role; scope-filtered server-side. */}
        <Route path="/studies" element={<TenantStudies />} />
        <Route path="/studies/:slug" element={<TenantStudyDetail />} />
        {/* ADR-0220 — Initiatives: every membership role reads; builders and admins create. */}
        <Route path="/initiatives" element={userId ? <TenantInitiatives entitlement={entitlement} callerAccountId={userId} /> : null} />
        <Route
          path="/initiatives/new"
          element={
            entitlement === "builder" || entitlement === "admin" ? (
              userId ? <TenantInitiativeCreate callerAccountId={userId} /> : null
            ) : (
              <Navigate to="/app/initiatives" replace />
            )
          }
        />
        <Route path="/initiatives/:id" element={<TenantInitiativeDetail entitlement={entitlement} />} />
        {/* ADR-0221 — Issues: every membership role reads; builders and admins raise. */}
        <Route path="/issues" element={userId ? <TenantIssues entitlement={entitlement} callerAccountId={userId} /> : null} />
        <Route path="/issues/new" element={entitlement === "builder" || entitlement === "admin" ? <TenantIssueCreate /> : <Navigate to="/app/issues" replace />} />
        <Route path="/issues/:id" element={<TenantIssueDetail entitlement={entitlement} />} />
        {/* ADR-0223 — Collab: every member reads, raises ideas and posts chatter; builders and admins manage meetings. */}
        <Route path="/collab" element={<CollabWeek />} />
        <Route path="/collab/ideas" element={<CollabIdeas />} />
        <Route path="/collab/ideas/new" element={<CollabIdeaCreate />} />
        <Route path="/collab/ideas/:id" element={<CollabIdeaDetail entitlement={entitlement} />} />
        <Route path="/collab/meetings" element={<CollabMeetings tab="recurring" entitlement={entitlement} />} />
        <Route path="/collab/meetings/one-off" element={<CollabMeetings tab="one-off" entitlement={entitlement} />} />
        <Route path="/collab/meetings/calendar" element={<CollabMeetings tab="calendar" entitlement={entitlement} />} />
        <Route path="/collab/meetings/series/new" element={entitlement === "builder" || entitlement === "admin" ? <CollabSeriesEdit /> : <Navigate to="/app/collab/meetings" replace />} />
        <Route path="/collab/series/:id" element={<CollabSeries />} />
        <Route path="/collab/series/:id/edit" element={entitlement === "builder" || entitlement === "admin" ? <CollabSeriesEdit /> : <Navigate to="/app/collab/meetings" replace />} />
        <Route path="/collab/series/:id/next" element={<CollabSession mode="next" entitlement={entitlement} />} />
        <Route path="/collab/series/:id/occurrences/:date" element={<CollabSession mode="occurrence" entitlement={entitlement} />} />
        <Route path="/collab/sessions/:id" element={<CollabSession mode="session" entitlement={entitlement} />} />
        {/* ADR-0214 Part 2 — Activity (the dashboard) and its drill pages: admins only until the viewer filters land. */}
        {/* ADR-0214 Activity: every person, about the people they may see (the hub resolves who). */}
        <Route path="/activity" element={<TenantActivity />} />
        <Route path="/activity/:card" element={<TenantActivityCard />} />
        {/* The cut 1 Subjects list is retired: its address opens Activity. */}
        <Route path="/subjects" element={<Navigate to="/app/activity" replace />} />
        <Route path="/subjects/people/:accountId" element={<TenantSubjectPerson />} />
        <Route path="/subjects/:number" element={<TenantSubjectDetail />} />
        {/* ADR-0185 Addendum 4 — the Optimize landing is ONE list, every role;
            the model home below opens on its Board tab with an informational
            Model tab beside it. */}
        <Route path="/optimize" element={<OptimizeHome entitlement={entitlement} models={optimizeModels} />} />
        {/* The retired board address redirects into the model home's Board tab. */}
        <Route path="/optimize/:modelId/board" element={<LegacyModelBoardRedirect />} />
        {/* ADR-0183 — proposal detail stays builder/admin. */}
        <Route
          path="/optimize/proposals/:proposalId"
          element={entitlement === "builder" || entitlement === "admin" ? <OptimizeProposal /> : <Navigate to="/app" replace />}
        />
        {/* Every membership role: viewers get the board and the read-only model
            description; the admin strip gates inside on entitlement. */}
        <Route path="/optimize/:modelId" element={<OptimizeModelHome entitlement={entitlement} />} />
        <Route
          path="/my-action-items"
          element={<Navigate to={`/app/action-items?my_rasci_roles=${RASCI_ROLES.join(",")}`} replace />}
        />
        <Route
          path="/admin/action-item-codes"
          element={entitlement === "admin" ? <AdminActionItemCodes /> : <Navigate to="/app/overview" replace />}
        />
        <Route
          path="/admin/customers"
          element={entitlement === "admin" ? <Customers /> : <Navigate to="/app/overview" replace />}
        />
        <Route
          path="/admin/customers/new"
          element={entitlement === "admin" ? <CustomerNew /> : <Navigate to="/app/overview" replace />}
        />
        <Route
          path="/admin/customers/:id"
          element={entitlement === "admin" ? <CustomerDetail /> : <Navigate to="/app/overview" replace />}
        />
        <Route
          path="/admin/integrations"
          element={entitlement === "admin" ? <Integrations /> : <Navigate to="/app/overview" replace />}
        />
        <Route
          path="/admin/integrations/new"
          element={entitlement === "admin" ? <IntegrationNew /> : <Navigate to="/app/overview" replace />}
        />
        <Route
          path="/admin/integrations/:id"
          element={entitlement === "admin" ? <IntegrationDetail /> : <Navigate to="/app/overview" replace />}
        />
        <Route
          path="/admin/escalation-policies"
          element={entitlement === "admin" ? <AdminEscalationPolicies /> : <Navigate to="/app/overview" replace />}
        />
        <Route
          path="/admin/escalation-policies/:id"
          element={entitlement === "admin" ? <AdminEscalationPolicyEditor /> : <Navigate to="/app/overview" replace />}
        />
        <Route
          path="/admin/notifications"
          element={entitlement === "admin" ? <AdminNotificationRouting /> : <Navigate to="/app/overview" replace />}
        />
        <Route path="/admin/groups" element={entitlement === "admin" ? <RedirectTenantAdminGroups /> : <Navigate to="/app/overview" replace />} />
        <Route
          path="/admin/groups/:groupId"
          element={entitlement === "admin" ? <RedirectTenantAdminGroupDetail /> : <Navigate to="/app/overview" replace />}
        />
        <Route path="/admin/connectors" element={entitlement === "admin" ? <TenantAdminConnectors /> : <Navigate to="/app/overview" replace />} />
        <Route path="/admin/settings" element={entitlement === "admin" ? <TenantAdminSettings /> : <Navigate to="/app/overview" replace />} />
        <Route path="/admin/ai-usage" element={entitlement === "admin" ? <TenantAdminAiUsage /> : <Navigate to="/app/overview" replace />} />
        {/* ADR-0148: legacy AI Knowledge page retired — Tenant Knowledge is the single surface. */}
        <Route path="/admin/ai-knowledge" element={<Navigate to="/app/admin/tenant-knowledge" replace />} />
        <Route path="/admin/learning" element={entitlement === "admin" ? <TenantAdminLearning /> : <Navigate to="/app/overview" replace />} />
        <Route path="/admin/tenant-knowledge" element={entitlement === "admin" ? <TenantAdminTenantKnowledge /> : <Navigate to="/app/overview" replace />} />
        <Route path="/admin/tenant-knowledge/new" element={entitlement === "admin" ? <TenantKnowledgeEntryEditor /> : <Navigate to="/app/overview" replace />} />
        <Route path="/admin/tenant-knowledge/entry/:name" element={entitlement === "admin" ? <TenantKnowledgeEntryEditor /> : <Navigate to="/app/overview" replace />} />
        <Route path="/admin/tenant-knowledge/proposal/:id" element={entitlement === "admin" ? <TenantKnowledgeProposalDetail /> : <Navigate to="/app/overview" replace />} />
        {/* ADR-0148: tenant AI guidance editor (Guidance tab lives inside Tenant Knowledge). */}
        <Route path="/admin/tenant-guidance/new" element={entitlement === "admin" ? <TenantGuidanceEntryEditor /> : <Navigate to="/app/overview" replace />} />
        <Route path="/admin/tenant-guidance/entry/:name" element={entitlement === "admin" ? <TenantGuidanceEntryEditor /> : <Navigate to="/app/overview" replace />} />
        {/* Legacy bookmark: Query Intelligence renamed to Tenant Knowledge. */}
        <Route path="/admin/query-intelligence" element={<Navigate to="/app/admin/tenant-knowledge" replace />} />
        <Route path="/admin/ai-settings" element={<Navigate to="/app/admin/settings" replace />} />
        {/* Build routes - builder/admin only */}
        <Route
          path="/build/providers"
          element={entitlement === "builder" || entitlement === "admin" ? <DataProviders /> : <Navigate to="/app" replace />}
        />
        <Route
          path="/build/providers/:id"
          element={entitlement === "builder" || entitlement === "admin" ? <DataProviderEditor /> : <Navigate to="/app" replace />}
        />
        <Route
          path="/build/visualizations"
          element={entitlement === "builder" || entitlement === "admin" ? <Visualizations /> : <Navigate to="/app" replace />}
        />
        <Route
          path="/build/visualizations/:visualizationId"
          element={entitlement === "builder" || entitlement === "admin" ? <ReportEditor /> : <Navigate to="/app" replace />}
        />
        {/* ADR-0068 §6.2 — list paths redirect to /app/workspace/*; editors stay. */}
        <Route path="/build/pages" element={<Navigate to="/app/workspace/pages" replace />} />
        <Route
          path="/build/pages/:pageId"
          element={entitlement === "builder" || entitlement === "admin" ? <BuildPageEditor /> : <Navigate to="/app" replace />}
        />
        <Route path="/build/menus" element={<Navigate to="/app/workspace/menus" replace />} />
        <Route
          path="/build/menus/:menuId"
          element={entitlement === "builder" || entitlement === "admin" ? <BuildMenuEditor /> : <Navigate to="/app" replace />}
        />
        <Route
          path="/build/operations"
          element={entitlement === "builder" || entitlement === "admin" ? <OperationsList /> : <Navigate to="/app" replace />}
        />
        <Route
          path="/build/operations/:id"
          element={entitlement === "builder" || entitlement === "admin" ? <OperationEditor /> : <Navigate to="/app" replace />}
        />
        <Route
          path="/build/semantic-models"
          element={entitlement === "admin" ? <SemanticModels /> : <Navigate to="/app" replace />}
        />
        <Route
          path="/build/semantic-models/:id"
          element={entitlement === "admin" ? <SemanticDesigner /> : <Navigate to="/app" replace />}
        />
        <Route path="/build/pdos" element={<Navigate to="/app/workspace/pdos" replace />} />
        <Route
          path="/build/pdos/:id"
          element={entitlement === "builder" || entitlement === "admin" ? <PdoDetail /> : <Navigate to="/app" replace />}
        />
        {/* ADR-0106 — Models */}
        <Route path="/build/models" element={<Navigate to="/app/workspace/models" replace />} />
        <Route
          path="/build/models/:id"
          element={entitlement === "builder" || entitlement === "admin" ? <ModelDetail /> : <Navigate to="/app" replace />}
        />
        <Route
          path="/build/content-transfer"
          element={entitlement === "builder" || entitlement === "admin" ? <TenantContentTransfer /> : <Navigate to="/app" replace />}
        />

        {/* ---------------------------------------------------------------- */}
        {/* ADR-0068 Phase 6 — Workspace (scope-aware list surfaces)         */}
        {/* ---------------------------------------------------------------- */}
        {userId && tenantId && (
          <Route
            path="/workspace/pages"
            element={
              entitlement === "builder" || entitlement === "admin" ? (
                <WorkspacePages callerAccountId={userId} tenantId={tenantId} entitlement={entitlement} />
              ) : (
                <Navigate to="/app" replace />
              )
            }
          />
        )}
        {userId && tenantId && (
          <Route
            path="/workspace/menus"
            element={
              entitlement === "builder" || entitlement === "admin" ? (
                <WorkspaceMenus callerAccountId={userId} tenantId={tenantId} entitlement={entitlement} />
              ) : (
                <Navigate to="/app" replace />
              )
            }
          />
        )}
        {/* ADR-0216 — Atlas Reports (read-only; authored from the door) */}
        <Route
          path="/workspace/atlas-reports"
          element={entitlement === "builder" || entitlement === "admin" ? <TenantAtlasReports /> : <Navigate to="/app" replace />}
        />
        <Route
          path="/workspace/atlas-reports/:id"
          element={entitlement === "builder" || entitlement === "admin" ? <TenantAtlasReportDetail /> : <Navigate to="/app" replace />}
        />
        {userId && tenantId && (
          <Route
            path="/workspace/pdos"
            element={
              entitlement === "builder" || entitlement === "admin" ? (
                <WorkspacePDOs callerAccountId={userId} tenantId={tenantId} entitlement={entitlement} />
              ) : (
                <Navigate to="/app" replace />
              )
            }
          />
        )}
        {/* ADR-0092 — upload-source PDO quickcreate flow */}
        <Route
          path="/workspace/pdos/new-from-upload"
          element={entitlement === "builder" || entitlement === "admin" ? <NewUploadPdo /> : <Navigate to="/app" replace />}
        />
        {/* ADR-0106 — Models catalog */}
        <Route
          path="/workspace/models"
          element={entitlement === "builder" || entitlement === "admin" ? <WorkspaceModels /> : <Navigate to="/app" replace />}
        />
        {userId && tenantId && (
          <Route
            path="/workspace/schedules"
            element={
              entitlement === "builder" || entitlement === "admin" ? (
                <WorkspaceSchedules callerAccountId={userId} tenantId={tenantId} entitlement={entitlement} />
              ) : (
                <Navigate to="/app" replace />
              )
            }
          />
        )}
        {/* Saved Queries list + editor live inside Atlas Query (Phase 7 builds the tray). */}
        <Route path="/workspace/queries" element={<Navigate to="/app/atlas-query" replace />} />
        <Route path="/workspace/queries/new" element={<Navigate to="/app/atlas-query" replace />} />
        <Route path="/workspace/queries/:id" element={<Navigate to="/app/atlas-query" replace />} />

        {/* Admin — Orphaned Content (§6, §9.7) */}
        <Route
          path="/admin/orphaned-content"
          element={entitlement === "admin" ? <OrphanedContent /> : <Navigate to="/app/overview" replace />}
        />

        {/* Schedule routes — /schedule/workflows list redirects to /workspace/schedules per ADR-0068. */}
        <Route path="/schedule/workflows" element={<Navigate to="/app/workspace/schedules" replace />} />
        <Route
          path="/schedule/workflows/:id"
          element={
            entitlement === "builder" || entitlement === "admin" ? (
              <ScheduleWorkflowCanvas />
            ) : (
              <Navigate to="/app" replace />
            )
          }
        />
        <Route
          path="/schedule/blackout-schedules"
          element={
            entitlement === "builder" || entitlement === "admin" ? (
              <ScheduleBlackouts />
            ) : (
              <Navigate to="/app" replace />
            )
          }
        />
        <Route
          path="/schedule/activity"
          element={
            entitlement === "builder" || entitlement === "admin" ? (
              <ScheduleActivity />
            ) : (
              <Navigate to="/app" replace />
            )
          }
        />
        {/* ADR-0098 — Report Subscriptions */}
        <Route
          path="/schedule/subscriptions"
          element={
            entitlement === "builder" || entitlement === "admin" ? (
              <TenantSubscriptions />
            ) : (
              <Navigate to="/app" replace />
            )
          }
        />
        <Route
          path="/schedule/subscriptions/new"
          element={
            entitlement === "builder" || entitlement === "admin" ? (
              <TenantSubscriptionNew />
            ) : (
              <Navigate to="/app" replace />
            )
          }
        />
        <Route
          path="/schedule/subscriptions/:id"
          element={
            entitlement === "builder" || entitlement === "admin" ? (
              <TenantSubscriptionDetail entitlement={entitlement} />
            ) : (
              <Navigate to="/app" replace />
            )
          }
        />
        <Route
          path="/monitor/watches"
          element={
            entitlement === "builder" || entitlement === "admin" ? (
              <MonitorWatchOperations />
            ) : (
              <Navigate to="/app" replace />
            )
          }
        />
        {/* Legacy routes - keep for backward compatibility */}
        <Route path="/reporting/data-providers" element={<DataProviders />} />
        <Route path="/reporting/data-providers/:id" element={<DataProviderEditor />} />
        <Route path="/reporting/visualizations" element={<Visualizations />} />
        <Route path="/reporting/visualizations/:visualizationId" element={<ReportEditor />} />
        <Route path="/reports" element={<Navigate to="/app/build/visualizations" replace />} />
        <Route path="/reports/:reportId" element={<ReportsDetailRedirect />} />
        <Route path="/query-test" element={<Navigate to="/app/atlas-query" replace />} />
        <Route
          path="/atlas-query"
          element={
            atlasQueryOpen(entitlement, atlasQuery) ? (
              <Suspense
                fallback={
                  <div className="card">
                    <div className="muted">Loading…</div>
                  </div>
                }
              >
                <AtlasQuery />
              </Suspense>
            ) : (
              <Navigate to="/app" replace />
            )
          }
        />
      </Routes>
    </AppShell>
  );
}

function TenantHome(props: { entitlement?: string }) {
  const [target, setTarget] = useState<HomeTarget | null>(null);
  useEffect(() => {
    void (async () => {
      try {
        const data = await apiFetch<{ target: HomeTarget }>("/api/tenant/pages/home");
        setTarget(data.target);
      } catch {
        setTarget({ kind: "default_page" });
      }
    })();
  }, [props.entitlement]);

  if (!target) {
    return (
      <div className="card">
        <div className="muted">Loading…</div>
      </div>
    );
  }

  if (target.kind === "page") {
    return <TenantPage entitlement={(props.entitlement as any) ?? "viewer"} routeKeyOverride={target.route_key} />;
  }

  return <TenantDefaultPage entitlement={props.entitlement} />;
}
